import React, { useEffect, useCallback, useMemo } from 'react';
import { Route, Router, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import AuthRequired from './components/AuthRequired';
import AuthProvider from './contexts/AuthProvider';
import '@gamesb42/ui-kit/dist/const.css';
import '@gamesb42/ui-kit/dist/antd.css';
import '@gamesb42/ui-kit/dist/main.css';
import './global.css';
import Auth from './store/Auth';
import applaudStore, { Status } from './store/Applaud';
import Home from './pages/Home';
import ReportingDocuments from './pages/ReportingDocuments';
import Transactions from './pages/Transactions';
import Connection from './pages/Connection';
import ExpectedPayments from './pages/ExpectedPayments';
import RevenueSources from './pages/RevenueSources';
import Aolist from './pages/Aolist';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import InviteUser from './pages/InviteUser';
import userStore from './store/User';
import connectSources from './store/ConnectSources';
import { about } from './pages/Connection/components/AboutCompany';
import { contactPersonInst } from './pages/Connection/components/ContactPerson';
import { companySignatoryInst } from './pages/Connection/components/CompanySignatory';
import { getUserStatusInfo } from './utils/userStatus';
import appStore from './store/App';
import platformStore from './pages/Home/SalesInfo/store';
import { appInfoInst } from 'pages/Connection/components/AppInfo';
import { corparateDoc } from 'pages/Connection/components/CorporateDocument';
import { director } from 'pages/Connection/components/Director';
import ReconcilationReport from 'pages/ReconcilationReport';

export const PAGES = {
  FUTURE_RECEIVABLES: '/future_receivables',
};

const AuthHOC = (props: { children: React.ReactNode }) => {
  const { children } = props;
  return <AuthRequired>{children}</AuthRequired>;
};

const App: React.FC = () => {
  const history = useHistory();

  const initializeStores = useCallback(async (): Promise<void> => {
    // Проверяем, что данные еще не загружены
    if (appStore.isLoadedMainInfo) {
      return;
    }

    try {
      // Сначала загружаем данные пользователя (они нужны для последующих запросов)
      const userPromises = [];
      if (!userStore.user) {
        userPromises.push(userStore.getUserInfo());
      }
      if (!userStore.me && Auth.user) {
        userPromises.push(userStore.getMe());
      }

      // Ждем загрузку данных пользователя
      if (userPromises.length > 0) {
        await Promise.all(userPromises);
      }

      // Теперь загружаем остальные данные включая onboarding (который использует userStore.me.company.id)
      const promises = [];

      if (!applaudStore.availableProducts?.length) {
        promises.push(applaudStore.getAvailableProducts());
      }

      if (!userStore.onboardingSteps) {
        promises.push(userStore.getOnboarding());
      }

      // Исправляем проверку для platformStore
      if (!platformStore.data?.platforms?.length) {
        promises.push(platformStore.getPlatformsData());
      }

      if (!connectSources.accounts?.length) {
        promises.push(connectSources.getAccounts());
      }

      // Исправляем проверку для connectSources
      if (!connectSources.sourcesListSelf?.length) {
        promises.push(connectSources.getSources());
      }

      // Убираем дублирующий запрос about.getCompanyInfo() - он будет вызван позже

      // Возвращаем вызов applaudStore.getApplaud() для начальной загрузки данных dashboard
      if (!applaudStore.data) {
        promises.push(applaudStore.getApplaud());
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      const companyId =
        userStore.me?.company?.id.toString() || Auth.user?.company?.id || '';

      if (companyId) {
        // Сначала загружаем основные данные компании
        await about.getCompanyInfo();

        const additionalPromises = [];

        // Всегда загружаем данные компонентов онбординга, чтобы они могли правильно установить isDataSended
        // на основе актуальных данных userStore.onboardingSteps
        additionalPromises.push(appInfoInst.getData(companyId));
        additionalPromises.push(corparateDoc.getCorporateDocData(companyId));
        additionalPromises.push(director.getDirectorData(companyId));
        additionalPromises.push(contactPersonInst.loadContactData());

        await Promise.all(additionalPromises);

        // Инициализируем Company Signatory после загрузки данных about
        // Передаем signer или null - метод init сам обработает оба случая
        companySignatoryInst.init(about?.signer || null);
      }

      // Устанавливаем флаг успешной загрузки только после всех запросов
      appStore.isLoadedMainInfo = true;
    } catch (error) {
      console.error('Error in initializeStores:', error);
      // При ошибке все равно устанавливаем флаг, чтобы избежать бесконечных попыток
      appStore.isLoadedMainInfo = true;
      throw error;
    }
  }, []); // Убираем Auth.user из зависимостей чтобы избежать повторных инициализаций

  useEffect(() => {
    const initApp = async () => {
      // Проверяем что авторизация инициализирована
      if (!Auth.isInitialized) {
        return;
      }

      // КРИТИЧНО: Не инициализируем приложение если пользователь не авторизован
      if (!Auth.isAuthenticated || !Auth.user) {
        console.log('User not authenticated, skipping app initialization');
        // Убеждаемся что флаг загрузки сброшен для неавторизованных пользователей
        appStore.isLoadedMainInfo = false;
        return;
      }

      try {
        await initializeStores();
        // handleRedirects(); // Убираем вызов несуществующей функции
      } catch (error) {
        console.error('App initialization error:', error);
        // При ошибке инициализации НЕ устанавливаем флаг isLoadedMainInfo
        // чтобы AuthRequired показал лоадер вместо пустого контента
      }
    };

    initApp();
  }, [Auth.isInitialized, Auth.isAuthenticated, Auth.user, initializeStores]); // Добавляем зависимости для правильной реакции на изменения

  return (
    <AuthProvider>
      <Router history={history}>
        <Route exact path="/login" render={() => <Login />} />
        <Route exact path="/reset-password" render={() => <ResetPassword />} />
        <Route exact path="/invite" render={() => <InviteUser />} />
        <Route
          exact
          path="/"
          render={() => (
            <AuthRequired>
              <Home />
            </AuthRequired>
          )}
        />
        <Route
          path="/reporting-docs"
          render={() => (
            <AuthRequired>
              <ReportingDocuments />
            </AuthRequired>
          )}
        />
        <Route
          path="/transactions"
          render={() => (
            <AuthRequired>
              <Transactions />
            </AuthRequired>
          )}
        />
        <Route
          path="/connection"
          render={() => (
            <AuthRequired>
              <Connection />
            </AuthRequired>
          )}
        />
        <Route
          path="/expected-payments"
          render={() => (
            <AuthRequired>
              <ExpectedPayments />
            </AuthRequired>
          )}
        />
        <Route
          path="/revenue_sources"
          render={() => (
            <AuthRequired>
              <RevenueSources />
            </AuthRequired>
          )}
        />
        <Route
          path="/ao-list"
          render={() => (
            <AuthRequired>
              <Aolist />
            </AuthRequired>
          )}
        />
        <Route
          path="/reconciliationReport"
          render={() => (
            <AuthRequired>
              <ReconcilationReport />
            </AuthRequired>
          )}
        />
      </Router>
    </AuthProvider>
  );
};

export default observer(App);
